/** The demo cases the admin deploys (fixtures in app/lambda/src/demo/cases): one industrial group's portfolio, written as if it had been worked on for a year. */

export type DemoLanguage = "en" | "de";
export type DemoText = Record<DemoLanguage, string>;

export type DemoCatalogStage = "proposed" | "evaluating" | "committed" | "executing";

export type DemoCatalogEntry = {
  id: string;
  name: DemoText;
  blurb: DemoText;
  stage: DemoCatalogStage;
  /** False: written as the owner's unpublished draft, which only they see. */
  published: boolean;
  closed: boolean;
  hasActuals: boolean;
};

export const DEMO_CATALOG = [
  {
    id: "h2-furnace",
    name: { en: "Hydrogen-Ready Heat Treatment", de: "Wasserstofffähige Wärmebehandlung" },
    blurb: { en: "Furnace replacement against a rising CO₂ price; pays back only after the window.", de: "Ofenersatz gegen steigenden CO₂-Preis; rechnet sich erst nach dem Zeitfenster." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "spare-parts-shop",
    name: { en: "Spare Parts Web Shop", de: "Ersatzteil-Webshop" },
    blurb: { en: "Visitor-to-order funnel; own shop or marketplace.", de: "Besucher-zu-Bestellung-Trichter; eigener Shop oder Marktplatz." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "additive-prototyping",
    name: { en: "Additive Manufacturing for Prototypes", de: "Additive Fertigung für Prototypen" },
    blurb: { en: "Buy metal printers or use a service bureau.", de: "Metalldrucker kaufen oder Dienstleister nutzen." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "gearbox-remanufacturing",
    name: { en: "Gearbox Remanufacturing Service", de: "Getriebe-Remanufacturing" },
    blurb: { en: "Refurbish used industrial gearboxes; demand is a wide guess.", de: "Gebrauchte Industriegetriebe aufarbeiten; die Nachfrage ist eine breite Schätzung." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "india-sales-office",
    name: { en: "Sales Office India", de: "Vertriebsbüro Indien" },
    blurb: { en: "Market entry for industrial drives; very uncertain order intake.", de: "Markteintritt für Industrieantriebe; sehr unsicherer Auftragseingang." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "battery-peak-shaving",
    name: { en: "Battery Storage Peak Shaving Siegen", de: "Batteriespeicher Lastspitzen Siegen" },
    blurb: { en: "Cut the forge's peak charges; savings hinge on tariffs and load profile.", de: "Lastspitzen der Schmiede kappen; Einsparung hängt an Tarif und Lastprofil." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "robotic-deburring",
    name: { en: "Robotic Deburring Kassel", de: "Roboter-Entgratung Kassel" },
    blurb: { en: "Automate manual deburring of gears; cycle times not yet tested.", de: "Manuelles Entgraten von Zahnrädern automatisieren; Taktzeiten noch nicht getestet." },
    stage: "proposed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "press-line-automation",
    name: { en: "Forging Press Line Automation", de: "Automatisierung Schmiedepressenlinie" },
    blurb: { en: "Large capex with a delayed ramp-up of the robot cells.", de: "Hohe Investition mit verzögertem Hochlauf der Roboterzellen." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "wind-gearboxes",
    name: { en: "Gearboxes for Wind Turbines", de: "Getriebe für Windkraftanlagen" },
    blurb: { en: "New product line, capex spread over the build-up, very uncertain, NPV goal.", de: "Neue Produktlinie, Investition über den Aufbau verteilt, sehr unsicher, NPV-Ziel." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "aluminium-recycling",
    name: { en: "Closed-Loop Aluminium Recycling", de: "Aluminium-Kreislaufrecycling" },
    blurb: { en: "Material savings with the risk of losing the remelting partner.", de: "Materialeinsparung mit dem Risiko, den Umschmelzpartner zu verlieren." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "gear-grinding-insourcing",
    name: { en: "Gear Grinding Insourcing Kassel", de: "Insourcing Zahnradschleifen Kassel" },
    blurb: { en: "Bring outsourced gear grinding in-house; mid-size capex.", de: "Fremdvergebenes Zahnradschleifen ins Haus holen; mittlere Investition." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "ev-rotor-shafts",
    name: { en: "Rotor Shafts for E-Drives", de: "Rotorwellen für E-Antriebe" },
    blurb: { en: "New product line for EV customers; two line concepts, units goal.", de: "Neue Produktlinie für E-Auto-Kunden; zwei Linienkonzepte, Stückzahlziel." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "vision-inspection",
    name: { en: "AI Vision Inspection Wrocław", de: "KI-Bildprüfung Breslau" },
    blurb: { en: "Inline inspection of die-cast housings to cut scrap and claims.", de: "Inline-Prüfung von Druckgussgehäusen gegen Ausschuss und Reklamationen." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "large-housing-casting",
    name: { en: "Large Housing Casting Cell", de: "Gießzelle für Großgehäuse" },
    blurb: { en: "Large die-casting cell for e-drive housings; big and uncertain.", de: "Große Druckgießzelle für E-Antriebsgehäuse; groß und unsicher." },
    stage: "evaluating",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "mes-rollout",
    name: { en: "MES Rollout Across Six Plants", de: "MES-Rollout in sechs Werken" },
    blurb: { en: "Licence cost spread over a plant-by-plant rollout.", de: "Lizenzkosten verteilt über einen Rollout Werk für Werk." },
    stage: "committed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "cnc-predictive-maintenance",
    name: { en: "Predictive Maintenance CNC Fleet", de: "Predictive Maintenance CNC-Flotte" },
    blurb: { en: "Less unplanned downtime on 140 machining centres.", de: "Weniger ungeplante Stillstände an 140 Bearbeitungszentren." },
    stage: "committed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "service-contracts",
    name: { en: "Service Contracts for the Installed Base", de: "Serviceverträge für die installierte Basis" },
    blurb: { en: "Recurring service revenue with churn at renewal.", de: "Wiederkehrender Serviceumsatz mit Kündigungen bei Verlängerung." },
    stage: "committed",
    published: true,
    closed: false,
    hasActuals: false,
  },
  {
    id: "energy-program",
    name: { en: "Energy Efficiency Program", de: "Energieeffizienz-Programm" },
    blurb: { en: "LED and compressed-air leaks; actuals ahead of plan.", de: "LED und Druckluftleckagen; Ist-Werte besser als geplant." },
    stage: "executing",
    published: true,
    closed: false,
    hasActuals: true,
  },
  {
    id: "bar-steel-consolidation",
    name: { en: "Bar Steel Supplier Consolidation", de: "Konsolidierung Stabstahl-Lieferanten" },
    blurb: { en: "Price savings; actuals below plan since the last price round.", de: "Preiseinsparung; Ist-Werte seit der letzten Preisrunde unter Plan." },
    stage: "executing",
    published: true,
    closed: false,
    hasActuals: true,
  },
  {
    id: "us-plant-expansion",
    name: { en: "US Plant Capacity Expansion", de: "Kapazitätserweiterung US-Werk" },
    blurb: { en: "The big one: a second hall, actuals on output volume.", de: "Der große Fall: eine zweite Halle, Ist-Werte zur Ausbringung." },
    stage: "executing",
    published: true,
    closed: false,
    hasActuals: true,
  },
  {
    id: "pilsen-warehouse-robotics",
    name: { en: "Warehouse Robotics Pilsen", de: "Lagerrobotik Pilsen" },
    blurb: { en: "Closed after evaluation: unlikely to pay off.", de: "Nach der Bewertung geschlossen: rechnet sich voraussichtlich nicht." },
    stage: "evaluating",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "coating-line-upgrade",
    name: { en: "Coating Line Upgrade", de: "Modernisierung Beschichtungslinie" },
    blurb: { en: "Built but not yet published: the owner's draft.", de: "Gebaut, aber noch nicht veröffentlicht: Entwurf des Owners." },
    stage: "proposed",
    published: false,
    closed: false,
    hasActuals: false,
  },
  {
    id: "laser-cladding-repair",
    name: { en: "Laser Cladding for Die Repair", de: "Laserauftragschweißen für Gesenke" },
    blurb: { en: "Closed as Proposed: too small to be worth the effort.", de: "Als Vorschlag geschlossen: zu klein für den Aufwand." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "hydrogen-forklifts",
    name: { en: "Hydrogen Forklifts Pilsen", de: "Wasserstoff-Stapler Pilsen" },
    blurb: { en: "Closed as Proposed: unlikely to pay off.", de: "Als Vorschlag geschlossen: rechnet sich voraussichtlich nicht." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "steel-service-center",
    name: { en: "Own Steel Service Center", de: "Eigenes Stahl-Service-Center" },
    blurb: { en: "Closed as Proposed: strategy changed.", de: "Als Vorschlag geschlossen: Strategie geändert." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "vr-operator-training",
    name: { en: "VR Training for Machine Operators", de: "VR-Schulung für Maschinenbediener" },
    blurb: { en: "Closed as Proposed: too small.", de: "Als Vorschlag geschlossen: zu klein." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "chip-briquetting",
    name: { en: "Chip Briquetting Kassel", de: "Spänebrikettierung Kassel" },
    blurb: { en: "Closed as Proposed: the scrap dealer offers it as a service.", de: "Als Vorschlag geschlossen: der Schrotthändler bietet es als Service an." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "mexico-assembly",
    name: { en: "Assembly Plant Mexico", de: "Montagewerk Mexiko" },
    blurb: { en: "Closed as Proposed: strategy changed after the US expansion.", de: "Als Vorschlag geschlossen: Strategie nach der US-Erweiterung geändert." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "blockchain-traceability",
    name: { en: "Blockchain Part Traceability", de: "Blockchain-Rückverfolgbarkeit" },
    blurb: { en: "Closed as Proposed: unlikely to pay off.", de: "Als Vorschlag geschlossen: rechnet sich voraussichtlich nicht." },
    stage: "proposed",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "induction-hardening",
    name: { en: "Induction Hardening Line Siegen", de: "Induktionshärtelinie Siegen" },
    blurb: { en: "Closed after evaluation: unlikely to pay off.", de: "Nach der Bewertung geschlossen: rechnet sich voraussichtlich nicht." },
    stage: "evaluating",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "tool-crib-automation",
    name: { en: "Automated Tool Crib Kassel", de: "Automatische Werkzeugausgabe Kassel" },
    blurb: { en: "Closed after evaluation: merged into the MES rollout.", de: "Nach der Bewertung geschlossen: im MES-Rollout aufgegangen." },
    stage: "evaluating",
    published: true,
    closed: true,
    hasActuals: false,
  },
  {
    id: "compressor-heat-recovery",
    name: { en: "Compressor Heat Recovery", de: "Wärmerückgewinnung Kompressoren" },
    blurb: { en: "Closed after commitment: the energy contractor took it over.", de: "Nach der Freigabe geschlossen: der Energiedienstleister hat es übernommen." },
    stage: "committed",
    published: true,
    closed: true,
    hasActuals: false,
  },
] as const satisfies readonly DemoCatalogEntry[];

export type DemoCaseId = (typeof DEMO_CATALOG)[number]["id"];

export const DEMO_PORTFOLIO_NAME: DemoText = { en: "Strategic Initiatives", de: "Strategische Initiativen" };
